import { ApiRequest, billingApi } from '@/api';
import { ApiVersion } from '@/lib/api/types';
import { BillingDialogStageEnum } from '@/lib/billing/billing';
import { PlanNameEnum } from '@/lib/enum/billing/BillingEnum';
import spacetime from 'spacetime';
import {
  BillingApi,
  BillingDetails,
  BillingOverview,
  BillingPackage,
  BillingPackageIntervalEnum,
  BillingProductFeaturesEnum,
  BillingQuotaDefinition,
  BillingQuotaDefinitionLookupKeyEnum,
  BillingSubscription,
} from '../../api/v1';

interface BillingDialogState {
  show: boolean;
  isRestore: boolean;
  isBillingOnly: boolean;
  chosenPackage: Number | null;
  stage: BillingDialogStageEnum | null;
}

export default {
  namespaced: true,

  state: {
    currentSubscription: null as BillingSubscription,
    hasBillingDetails: null as BillingDetails,
    billingOverview: null as BillingOverview,
    billingQuotaDefinitions: [] as BillingQuotaDefinition[],
    billingPackages: [] as BillingPackage[],
    billingDialogState: {
      show: false,
      isRestore: false,
      isBillingOnly: false,
      chosenPackage: null,
      stage: null,
    } as BillingDialogState,
  },

  getters: {
    getCompanyFeatures(state): BillingProductFeaturesEnum[] {
      return (
        state.currentSubscription?.billingProductPrices.flatMap(
          (p) => p.billingProduct.features,
        ) || []
      );
    },

    isTrialVersion(state, getters, rootState, rootGetters): boolean {
      if (
        !state.currentSubscription ||
        !state.currentSubscription.trialEndsOn
      ) {
        return false;
      }

      const now = spacetime.now(rootGetters.timezone).startOf('day');
      const trialEnds = state.currentSubscription.trialEndsOn.toDate(
        rootGetters.timezone,
      );

      return now.isBefore(trialEnds);
    },

    trialDaysLeft(state, getters, rootState, rootGetters): number {
      if (!state.currentSubscription || !state.currentSubscription.trialEndsOn)
        return 0;

      const now = spacetime.now(rootGetters.timezone).startOf('day');
      const trialEnds = state.currentSubscription.trialEndsOn.toDate(
        rootGetters.timezone,
      );
      return now.diff(trialEnds, 'day');
    },

    billingQuotaDefinition(state): Function {
      return (
        quota: BillingQuotaDefinitionLookupKeyEnum,
      ): BillingQuotaDefinition | null => {
        return state.billingQuotaDefinitions.find((q) => q.lookupKey === quota);
      };
    },

    currentPackage(state): BillingPackage {
      return state.billingPackages.find(
        (bp) => bp.id === state.currentSubscription.billingPackageId,
      );
    },

    downgradePackage(state): BillingPackage | null {
      return state.billingPackages.find(
        (bp) => bp.id === state.currentSubscription.downgradeBillingPackageId,
      );
    },

    isCancelling(state): boolean {
      return (
        state.currentSubscription?.endsOn &&
        !state.currentSubscription?.downgradeBillingPackageId
      );
    },

    isDowngrading(state): boolean {
      return !!state.currentSubscription.downgradeBillingPackageId;
    },

    isUpgradable(state, getters) {
      const { currentPackage } = getters;

      return (
        currentPackage.name.includes(PlanNameEnum.ALittleBitShiftie) ||
        currentPackage.name.includes(PlanNameEnum.VeryShiftie)
      );
    },

    nextRenewalDate(state, getters) {
      /**
       * Unfortunately, Stripe only operates in UTC, so the anchor dates here are expected to
       * be represented as UTC dates, regardless of the company / browser timezone.
       */
      const { billingCycleAnchor, trialEndsOn } = state.currentSubscription;
      const { interval } = getters.currentPackage;
      const timezone = 'UTC';

      if (getters.isTrialVersion) {
        return trialEndsOn.toDate(timezone);
      }

      const anchor = spacetime(billingCycleAnchor * 1000).goto(timezone);
      const today = spacetime.now(timezone).startOf('day');

      if (interval === BillingPackageIntervalEnum.Monthly) {
        if (today.date() === 1 && !today.isSame(anchor, 'day')) {
          return today.toNativeDate();
        }

        return today.clone().add(1, 'month').startOf('month').toNativeDate();
      }

      let candidate = today
        .clone()
        .month(anchor.month())
        .date(anchor.date())
        .startOf('day');

      /**
       * Clamp for invalid dates, like 29th feb...
       */
      if (candidate.month() !== anchor.month()) {
        candidate = candidate
          .month(anchor.month())
          .date(candidate.daysInMonth())
          .startOf('day');
      }

      if (
        candidate.isBefore(today) ||
        (candidate.isSame(today, 'day') && anchor.isSame(today, 'day'))
      ) {
        candidate = candidate.add(1, 'year');

        if (candidate.month() !== anchor.month()) {
          candidate = candidate
            .month(anchor.month())
            .date(candidate.daysInMonth())
            .startOf('day');
        }
      }

      return candidate.toNativeDate();
    },
  },

  mutations: {
    SET_CURRENT_SUBSCRIPTION(state, data) {
      state.currentSubscription = data;
    },
    HAS_BILLING_DETAILS(state, data: boolean) {
      state.hasBillingDetails = data;
    },
    SET_BILLING_OVERVIEW(
      state,
      billingOverview: BillingOverview = {
        hasDetails: state.billingOverview.hasDetails,
        hasPaymentMethods: state.billingOverview.hasPaymentMethods,
      },
    ) {
      state.billingOverview = billingOverview;
    },
    SET_BILLING_DIALOG_STATE(
      state,
      billingDialogState: Partial<BillingDialogState>,
    ) {
      state.billingDialogState = {
        ...state.billingDialogState,
        ...billingDialogState,
      };
    },
    SET_BILLING_QUOTA_DEFINITIONS(
      state,
      billingQuotaDefinitions: BillingQuotaDefinition[],
    ) {
      state.billingQuotaDefinitions = billingQuotaDefinitions;
    },
    SET_BILLING_PACKAGES(state, billingPackages: BillingPackage[]) {
      state.billingPackages = billingPackages;
    },
  },

  actions: {
    async fetchBillingQuotaDefinitions({ commit }) {
      const { data: billingQuotaDefinitions } = await new ApiRequest<
        ApiVersion.V1,
        BillingApi,
        'listBillingQuotaDefinitions'
      >(billingApi, 'listBillingQuotaDefinitions').fetchAll();

      commit('SET_BILLING_QUOTA_DEFINITIONS', billingQuotaDefinitions);
    },
    async fetchBillingPackages({ commit }) {
      const { data: billingPackages } = await new ApiRequest<
        ApiVersion.V1,
        BillingApi,
        'listBillingPackages'
      >(billingApi, 'listBillingPackages').fetchAll();

      commit('SET_BILLING_PACKAGES', billingPackages);
    },
    async fetchBillingSubscription({ commit }) {
      const { data: currentSubscription } =
        await billingApi.showLatestSubscription({});

      commit('SET_CURRENT_SUBSCRIPTION', currentSubscription);
    },

    async setBillingOverview({ commit }) {
      const { data: billingOverview } = await billingApi.showBillingOverview();
      commit('SET_BILLING_OVERVIEW', billingOverview);
    },
  },
};
